import React, { Suspense, lazy, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import LandingNavbar from './components/LandingNavbar';
import DeferredLandingSection from './components/DeferredLandingSection';
import HeroSection from './sections/HeroSection';
import LaunchBanner from './sections/LaunchBanner';
import StickyConversionCta from './sections/StickyConversionCta';
import { useTranslation } from '../../i18n/LanguageContext';
import baseUrl from '../Configs';
import { fetchUserRoleInfo, updateUserRoleStorage, clearRoleStorage } from '../../utils/roleUtils';
import { clearAuthFlag } from '../../utils/authCookie';
import { trackAnalyticsEvent } from '../analytics';
import { preloadSuperDashboard } from '../routePreloaders';

const PilotSection = lazy(() => import('./sections/PilotSection'));
const RoleValueSection = lazy(() => import('./sections/RoleValueSection'));
const PromoVideoSection = lazy(() => import('./sections/PromoVideoSection'));
const PainPointsSection = lazy(() => import('./sections/PainPointsSection'));
const FeaturesSection = lazy(() => import('./sections/FeaturesSection'));
const ProductShowcase = lazy(() => import('./sections/ProductShowcase'));
const TeacherParentSection = lazy(() => import('./sections/TeacherParentSection'));
const AppShowcaseSection = lazy(() => import('./sections/AppShowcaseSection'));
const OnboardingSection = lazy(() => import('./sections/OnboardingSection'));
const ComplianceSection = lazy(() => import('./sections/ComplianceSection'));
const TrustSection = lazy(() => import('./sections/TrustSection'));
const FAQSection = lazy(() => import('./sections/FAQSection'));
const ContactSection = lazy(() => import('./sections/ContactSection'));
const FooterSection = lazy(() => import('./sections/FooterSection'));
const LoginForm = lazy(() => import('../LoginForm'));
const Register = lazy(() => import('../Register'));

const modalFallback = (
  <div className="lp-modal-loading" role="status" aria-label="Loading form">
    Loading…
  </div>
);

const ClassicLandingPage = () => {
  const navigate = useNavigate();
  const [authChecking, setAuthChecking] = useState(
    () => Boolean(localStorage.getItem('token')),
  );

  useEffect(() => {
    trackAnalyticsEvent({
      eventType: 'LANDING_VISIT',
      eventCategory: 'landing',
      status: 'INFO',
    });
  }, []);

  // Auto-redirect if user is already logged in
  useEffect(() => {
    const verifyExistingToken = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        // No token on this origin = logged out. Clear the shared ss_auth flag
        // in case it lingered (e.g. a logout path that missed it), so the
        // marketing site stops treating this visitor as logged in.
        clearAuthFlag();
        setAuthChecking(false);
        return;
      }

      preloadSuperDashboard().catch(() => {});

      try {
        const response = await fetch(`${baseUrl}/v1/auth/verify`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();

          // Save basic user data from verification
          if (data.user) {
            localStorage.setItem('userId', data.user._id);
            localStorage.setItem('userName', data.user.personname || data.user.username);
            if (data.user.school) {
              localStorage.setItem('schoolId', data.user.school);
            }

            // Fetch comprehensive role information
            try {
              const roleInfo = await fetchUserRoleInfo(token);
              if (roleInfo) {
                updateUserRoleStorage(roleInfo);
                const highestRole = localStorage.getItem('userHighestPriorityRole');
                if (highestRole) {
                  localStorage.setItem('userRole', highestRole);
                }
              } else {
                localStorage.setItem('userRole', data.user.role);
              }
            } catch {
              localStorage.setItem('userRole', data.user.role);
            }
          }

          navigate('/super');
          return; // Don't set authChecking false — we're navigating away
        } else {
          // Token is invalid/expired — clear stale auth data
          localStorage.removeItem('token');
          clearAuthFlag();
          localStorage.removeItem('userId');
          localStorage.removeItem('userName');
          localStorage.removeItem('schoolId');
          clearRoleStorage();
        }
      } catch (error) {
        console.error('Auto-login verification failed:', error);
        // Network error — clear stale data
        localStorage.removeItem('token');
        clearAuthFlag();
        localStorage.removeItem('userId');
        localStorage.removeItem('userName');
        localStorage.removeItem('schoolId');
        clearRoleStorage();
      }

      setAuthChecking(false);
    };

    verifyExistingToken();
  }, [navigate]);
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState('login'); // 'login' | 'register'

  const openLogin = () => {
    trackAnalyticsEvent({ eventType: 'LOGIN_MODAL_OPENED', eventCategory: 'landing', status: 'INFO' });
    setModalTab('login');
    setModalOpen(true);
  };
  const openRegister = () => {
    trackAnalyticsEvent({ eventType: 'REGISTER_MODAL_OPENED', eventCategory: 'landing', status: 'INFO' });
    setModalTab('register');
    setModalOpen(true);
  };

  // Open the login/register modal directly when linked from elsewhere
  // (e.g. the marketing site's "Login" button -> app.spellstudy.com/?login).
  // For a logged-in visitor the auto-login effect above redirects to /super
  // first, so this only takes effect for logged-out users.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('register')) {
      openRegister();
    } else if (params.has('login')) {
      openLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show a brief loading state while checking auth
  if (authChecking) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#0a0a0a',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: '#6c63ff',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="landing-page">
      <LandingNavbar onLoginClick={openLogin} onRegisterClick={openRegister} />

      <LaunchBanner />
      <HeroSection />
      <DeferredLandingSection anchorId="pilot" component={PilotSection} minHeight={620} />
      <DeferredLandingSection anchorId="role-value" component={RoleValueSection} minHeight={620} />
      <DeferredLandingSection anchorId="promo-video" component={PromoVideoSection} minHeight={760} />
      <DeferredLandingSection anchorId="challenges" component={PainPointsSection} minHeight={620} />
      <DeferredLandingSection anchorId="features" component={FeaturesSection} minHeight={700} />
      <DeferredLandingSection anchorId="showcase" component={ProductShowcase} minHeight={760} />
      <DeferredLandingSection anchorId="ecosystem" component={TeacherParentSection} minHeight={650} />
      <DeferredLandingSection anchorId="app-showcase" component={AppShowcaseSection} minHeight={700} />
      <DeferredLandingSection anchorId="how-it-works" component={OnboardingSection} minHeight={600} />
      <DeferredLandingSection anchorId="security" component={ComplianceSection} minHeight={600} />
      <DeferredLandingSection anchorId="trust" component={TrustSection} minHeight={520} />
      <DeferredLandingSection anchorId="faq" component={FAQSection} minHeight={650} />
      <DeferredLandingSection anchorId="contact" component={ContactSection} minHeight={620} />
      <DeferredLandingSection component={FooterSection} minHeight={320} reveal={false} />
      <StickyConversionCta />

      {/* Auth Modal — Login / Register tabs */}
      {modalOpen && (
        <div className="lp-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="lp-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="lp-modal-close" onClick={() => setModalOpen(false)}>
              <i className="fa-solid fa-xmark" />
            </button>

            {/* Tab Buttons */}
            <div className="lp-modal-tabs">
              <button
                className={`lp-modal-tab ${modalTab === 'login' ? 'active' : ''}`}
                onClick={() => setModalTab('login')}
              >
                {t('common.login')}
              </button>
              <button
                className={`lp-modal-tab ${modalTab === 'register' ? 'active' : ''}`}
                onClick={() => setModalTab('register')}
              >
                {t('common.registerSchool')}
              </button>
            </div>

            <Suspense fallback={modalFallback}>
              {modalTab === 'login' ? <LoginForm onSwitchToRegister={() => setModalTab('register')} /> : <Register />}
            </Suspense>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassicLandingPage;
